import { useState, useEffect, useCallback, RefObject } from 'react';

interface UseGoogleMapOptions {
  center: google.maps.LatLngLiteral;
  zoom: number;
  showMapTypeSelector?: boolean;
  mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
}

interface UseGoogleMapReturn {
  map: google.maps.Map | null;
  isFollowing: boolean;
  setIsFollowing: (following: boolean) => void;
  panTo: (position: google.maps.LatLngLiteral) => void;
  setZoom: (zoom: number) => void;
}

/**
 * Custom hook for Google Maps instance management
 * Handles initialization, following mode, and user interactions
 * Based on FullMap.razor and googlemap.ts initialization patterns
 */
export const useGoogleMap = (
  containerRef: RefObject<HTMLDivElement>,
  options: UseGoogleMapOptions
): UseGoogleMapReturn => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(true);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || map) return;

    // Wait for Google Maps API to load
    const initializeMap = () => {
      // Check if google is defined and fully loaded (including ControlPosition)
      if (typeof google === 'undefined' ||
          !google.maps ||
          !google.maps.Map ||
          !google.maps.ControlPosition) {
        setTimeout(initializeMap, 100);
        return;
      }

      // Read from runtime config (injected by Docker) or fall back to build-time env var
      const mapId = window.RUNTIME_CONFIG?.VITE_GOOGLE_MAPS_MAP_ID || import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

      const mapInstance = new google.maps.Map(containerRef.current!, {
        center: options.center,
        zoom: options.zoom,
        mapId: mapId,
        mapTypeId: options.mapType || 'roadmap',
        scaleControl: true,
        rotateControl: false,
        tilt: 0,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        disableDoubleClickZoom: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
        mapTypeControl: options.showMapTypeSelector ?? true,
      });

      setMap(mapInstance);
    };

    initializeMap();

    // Cleanup
    return () => {
      if (map) {
        google.maps.event.clearInstanceListeners(map);
      }
    };
  }, [containerRef, options.center.lat, options.center.lng, options.zoom]);

  // Add unlock listeners when map is created
  useEffect(() => {
    if (!map) return;

    const unlockOnDrag = () => setIsFollowing(false);
    const unlockOnZoom = () => setIsFollowing(false);

    const dragListener = map.addListener('drag', unlockOnDrag);
    const zoomListener = map.addListener('zoom_changed', unlockOnZoom);

    return () => {
      google.maps.event.removeListener(dragListener);
      google.maps.event.removeListener(zoomListener);
    };
  }, [map]);

  // Pan to position
  const panTo = useCallback((position: google.maps.LatLngLiteral) => {
    if (map) {
      map.panTo(position);
    }
  }, [map]);

  // Set zoom level
  const setZoom = useCallback((zoom: number) => {
    if (map) {
      map.setZoom(zoom);
    }
  }, [map]);

  return {
    map,
    isFollowing,
    setIsFollowing,
    panTo,
    setZoom,
  };
};
